import { useEffect, useMemo, useState } from 'react';
import { collection, getDocs, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { AuthContext, buildAuthValue, useAuth } from '../contexts/AuthContext';
import { previewSupplierViews } from '../utils/supplierViewRequests';
import { PreviewContext } from '../contexts/PreviewContext';
import { LocaleProvider } from '../contexts/LocaleContext';
import { inPortals } from '../contexts/MarketContext';
import SupplierDashboard from '../pages/supplier/SupplierDashboard';
import SupplierTourDetail from '../pages/supplier/SupplierTourDetail';
import TourLeaderTourDetail from '../pages/tour-leader/TourLeaderTourDetail';
import CustomerTourDetail from '../pages/customer/RequestDetail';
import FootballOpsTourDetail from '../pages/football-ops/FootballOpsTourDetail';
import { SupplierLocale } from '../pages/supplier/SupplierLocale';

// ── Admin portal preview ─────────────────────────────────────────────────────
// Renders another user's portal inside the staff app, as that user would see
// it: the real portal pages, given that user's identity through AuthContext
// (same derivation as sign-in, via buildAuthValue) and their own navigation
// through PreviewContext.
//
// Firestore still runs as the admin, so a preview must not write. The frame
// swallows clicks, typing, drags and submits, except inside a <nav> (the
// portals' tab bars and breadcrumbs), [data-preview-nav] / [data-preview-allow]
// (read-only controls such as the itinerary filter) and links opening a new
// tab. Every action button is therefore inert.
//
// Fixed elements (the portals' bottom tab bars, overlays) are kept inside the
// frame by its transform, which makes it their containing block.

const ALLOWED = 'nav, [data-preview-nav], [data-preview-allow], a[target="_blank"]';

function stopUnlessAllowed(e) {
  if (e.target instanceof Element && e.target.closest(ALLOWED)) return;
  e.preventDefault();
  e.stopPropagation();
}

function stopEnterUnlessAllowed(e) {
  if (e.key === 'Enter') stopUnlessAllowed(e);
}

const BLOCKERS = {
  onClickCapture: stopUnlessAllowed,
  onChangeCapture: stopUnlessAllowed,
  onInputCapture: stopUnlessAllowed,
  onSubmitCapture: stopUnlessAllowed,
  onDragStartCapture: stopUnlessAllowed,
  onDropCapture: stopUnlessAllowed,
  onKeyDownCapture: stopEnterUnlessAllowed,
};

const KIND_LABELS = {
  supplier: 'Supplier',
  tour_leader: 'Tour leader',
  customer: 'Customer',
  football_ops: 'Football Operations',
};

const noop = async () => {};

/** Where a viewer lands, and what each path renders. */
function startPath(viewer, requestId) {
  if (viewer.kind === 'supplier') return requestId ? `/supplier/tour/${requestId}` : '/supplier';
  if (viewer.kind === 'tour_leader') return `/tour-leader/tours/${requestId}`;
  if (viewer.kind === 'customer') return `/customer/requests/${requestId}`;
  return `/football-ops/tours/${requestId}`;
}

function renderPath(viewer, path, locale) {
  const m = (re) => path.match(re)?.[1];
  if (viewer.kind === 'supplier') {
    // The supplier's own EN / ES switch works in the preview too.
    if (path === '/supplier') return <SupplierLocale><SupplierDashboard /></SupplierLocale>;
    const id = m(/^\/supplier\/tour\/([^/]+)$/);
    if (id) return <SupplierLocale><SupplierTourDetail key={id} requestId={id} /></SupplierLocale>;
  }
  if (viewer.kind === 'tour_leader') {
    const id = m(/^\/tour-leader\/tours\/([^/]+)$/);
    if (id) return <TourLeaderTourDetail key={id} id={id} />;
  }
  if (viewer.kind === 'customer') {
    const id = m(/^\/customer\/requests\/([^/]+)$/);
    if (id) return <CustomerTourDetail key={id} id={id} />;
  }
  if (viewer.kind === 'football_ops') {
    const id = m(/^\/football-ops\/tours\/([^/]+)$/);
    if (id) return <LocaleProvider locale={locale}><FootballOpsTourDetail key={id} id={id} /></LocaleProvider>;
  }
  return null;
}

/**
 * One user's portal, read-only, in a framed box.
 *
 * viewer: { kind: 'supplier'|'tour_leader'|'customer'|'football_ops', uid,
 *           email, name, roles, portals?, footballOpsPermissions?, supplier? }
 * requestId: the tour to open (suppliers can also start on their tour list).
 */
export function PortalPreviewFrame({ viewer, requestId = null }) {
  const { currentUser: admin } = useAuth();
  const home = startPath(viewer, requestId);
  const [path, setPath] = useState(home);
  const [width, setWidth] = useState(viewer.kind === 'football_ops' ? 'desktop' : 'mobile');
  const [locale, setLocale] = useState('es');

  useEffect(() => { setPath(home); }, [home]);

  // Keyed on the identity's contents: callers rebuild `viewer` on every render,
  // and a new currentUser would make every portal listener re-subscribe.
  const identityKey = JSON.stringify([viewer.uid, viewer.email, viewer.name, viewer.roles, viewer.portals, viewer.footballOpsPermissions]);
  const auth = useMemo(() => buildAuthValue({
    currentUser: { uid: viewer.uid || `preview-${viewer.kind}`, email: (viewer.email || '').toLowerCase(), displayName: viewer.name || '' },
    userRoles: viewer.roles,
    userPortalsRaw: viewer.portals || null,
    footballOpsStored: viewer.footballOpsPermissions || null,
    login: noop,
    logout: noop,
  }), [identityKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Unsaved supplier permissions: the server builds the views it would build
  // with them (same code as the real ones), without saving anything.
  const overrideKey = viewer.permissionsOverride ? JSON.stringify(viewer.permissionsOverride) : '';
  const [builtViews, setBuiltViews] = useState(null);
  const [buildError, setBuildError] = useState('');
  useEffect(() => {
    if (!overrideKey || !viewer.supplier) { setBuiltViews(null); return undefined; }
    let cancelled = false;
    setBuiltViews(null); setBuildError('');
    previewSupplierViews(admin, viewer.supplier.id, JSON.parse(overrideKey))
      .then((views) => { if (!cancelled) setBuiltViews(views); })
      .catch((err) => { console.error(err); if (!cancelled) setBuildError('Could not build the preview with these permissions.'); });
    return () => { cancelled = true; };
  }, [overrideKey, viewer.supplier, admin]);

  const preview = useMemo(() => ({
    supplier: viewer.supplier || null,
    supplierViews: overrideKey ? (builtViews || []) : null,
    // Pages outside the preview (sign-out, a staff page) leave it where it is.
    navigate: (to) => {
      if (typeof to === 'number') { setPath(home); return; }
      setPath(String(to).split(/[?#]/)[0]);
    },
  }), [viewer.supplier, home, overrideKey, builtViews]);

  const content = overrideKey && !builtViews
    ? <p className="p-10 text-center text-[13px] text-[#9CA3AF]">{buildError || 'Building the preview with your unsaved permissions…'}</p>
    : renderPath(viewer, path, locale);

  return (
    <div className="rounded-[14px] border-2 border-[#F59E0B] overflow-hidden bg-white">
      <div className="bg-[#FFFBEB] border-b border-[#FCD34D] px-4 py-2.5 flex flex-wrap items-center justify-between gap-2">
        <p className="text-[12px] text-[#92400E]">
          Previewing as <strong>{viewer.name || viewer.email}</strong> ({KIND_LABELS[viewer.kind]}).
          {' '}Read-only: tabs work, buttons and forms are switched off.
        </p>
        <div className="flex items-center gap-2">
          {path !== home && (
            <button onClick={() => setPath(home)} className="text-[11.5px] font-semibold text-[#92400E] hover:underline">
              ← Back to start
            </button>
          )}
          {viewer.kind === 'football_ops' && (
            <select value={locale} onChange={(e) => setLocale(e.target.value)} aria-label="Language"
              className="border border-[#FCD34D] rounded-[7px] px-2 py-1 text-[11.5px] bg-white text-[#92400E]">
              <option value="es">Español</option>
              <option value="en">English</option>
            </select>
          )}
          <div className="flex rounded-[7px] border border-[#FCD34D] overflow-hidden">
            {['mobile', 'desktop'].map((w) => (
              <button key={w} onClick={() => setWidth(w)}
                className={`px-2.5 py-1 text-[11.5px] font-semibold capitalize ${width === w ? 'bg-[#F59E0B] text-white' : 'bg-white text-[#92400E]'}`}>
                {w}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="bg-[#E5E7EB] py-3">
        <div
          className={`relative h-[72vh] mx-auto bg-page shadow-sm ${width === 'mobile' ? 'max-w-[420px] rounded-[16px] overflow-hidden' : 'max-w-full'}`}
          style={{ transform: 'translateZ(0)' }}
        >
          <div className="absolute inset-0 overflow-y-auto overflow-x-hidden" {...BLOCKERS}>
            <AuthContext.Provider value={auth}>
              <PreviewContext.Provider value={preview}>
                <LocaleProvider locale="en">
                  {content || (
                    <div className="p-10 text-center">
                      <p className="text-[13px] text-[#6B7280]">That page isn't part of the preview.</p>
                      <button onClick={() => setPath(home)} data-preview-nav className="mt-2 text-[12.5px] font-semibold text-pb hover:text-navy">
                        Back to the tour
                      </button>
                    </div>
                  )}
                </LocaleProvider>
              </PreviewContext.Provider>
            </AuthContext.Provider>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * A supplier record as a preview viewer. Pass `permissionsOverride` to
 * preview permission changes that haven't been saved.
 */
export function supplierViewer(supplier, { permissionsOverride = null } = {}) {
  return {
    kind: 'supplier',
    uid: `preview-supplier-${supplier.id}`,
    email: supplier.email,
    name: supplier.name,
    roles: ['supplier'],
    supplier,
    permissionsOverride,
  };
}

/** Lower-cased emails of every login with the supplier role. */
export function useSupplierLoginEmails() {
  const [emails, setEmails] = useState(null);
  useEffect(() => {
    const found = {};
    const unsubs = [
      query(collection(db, 'users'), where('roles', 'array-contains', 'supplier')),
      query(collection(db, 'users'), where('role', '==', 'supplier')),
    ].map((q, i) => onSnapshot(q, (snap) => {
      found[i] = snap.docs.map((d) => (d.data().email || '').toLowerCase()).filter(Boolean);
      setEmails(new Set(Object.values(found).flat()));
    }, () => setEmails(new Set())));
    return () => unsubs.forEach((u) => u());
  }, []);
  return emails;
}

function rolesOf(u) {
  return Array.isArray(u.roles) ? u.roles : u.role ? [u.role] : [];
}

/**
 * The Previews tab on a tour: pick anyone who can see this tour and view it
 * as they do.
 */
export function TourPreviewsTab({ requestId, request }) {
  const [suppliers, setSuppliers] = useState([]);
  const [footballOps, setFootballOps] = useState([]);
  const [selectedKey, setSelectedKey] = useState(null);

  // Suppliers who can see this tour: exactly those with a view of it.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const views = await getDocs(query(collection(db, 'supplierViews'), where('requestId', '==', requestId)));
      const ids = [...new Set(views.docs.map((d) => d.data().supplierId))];
      const all = ids.length ? await getDocs(collection(db, 'suppliers')) : { docs: [] };
      const list = all.docs
        .filter((d) => ids.includes(d.id))
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      if (!cancelled) setSuppliers(list);
    })().catch((err) => console.error('Failed to load suppliers for preview', err));
    return () => { cancelled = true; };
  }, [requestId]);
  const loginEmails = useSupplierLoginEmails();

  // Football Operations users (new roles array, or the legacy single role).
  useEffect(() => {
    const byId = {};
    const publish = () => setFootballOps(Object.values(byId).sort((a, b) => (a.displayName || a.email || '').localeCompare(b.displayName || b.email || '')));
    const unsubs = [
      query(collection(db, 'users'), where('roles', 'array-contains', 'football_ops')),
      query(collection(db, 'users'), where('role', '==', 'football_ops')),
    ].map((q) => onSnapshot(q, (snap) => {
      snap.docs.forEach((d) => { byId[d.id] = { id: d.id, ...d.data() }; });
      publish();
    }, (err) => console.error('Failed to load football ops users', err)));
    return () => unsubs.forEach((u) => u());
  }, []);

  const leaders = request?.tourLeaders?.length
    ? request.tourLeaders
    : request?.tourLeaderId ? [{ id: request.tourLeaderId, name: request.tourLeaderName, email: request.tourLeaderEmail }] : [];

  const groups = [
    {
      title: 'Suppliers',
      empty: 'No suppliers on this tour yet. Suppliers appear once they are booked or chosen as the supplier on an activity.',
      viewers: suppliers.map((s) => ({
        key: `supplier:${s.id}`,
        viewer: supplierViewer(s),
        note: loginEmails && !loginEmails.has((s.email || '').toLowerCase()) ? 'No portal login — they only get emails' : null,
      })),
    },
    {
      title: 'Tour leaders',
      empty: 'No tour leaders assigned.',
      viewers: leaders.map((l) => ({
        key: `tour_leader:${l.id}`,
        viewer: { kind: 'tour_leader', uid: l.id, email: l.email, name: l.name || l.email || 'Tour leader', roles: ['tour_leader'] },
      })),
    },
    {
      title: 'Customer',
      empty: 'No customer contact on this tour.',
      viewers: request?.customerEmail ? [{
        key: 'customer',
        viewer: { kind: 'customer', uid: request.customerId || null, email: request.customerEmail, name: request.contactName || request.customerEmail, roles: ['customer'] },
        note: request.customerId ? null : 'No portal account linked — they only get emails',
      }] : [],
    },
    {
      title: 'Football Operations',
      empty: 'No Football Operations users.',
      viewers: footballOps.map((u) => {
        const roles = rolesOf(u);
        const viewer = {
          kind: 'football_ops', uid: u.id, email: u.email, name: u.displayName || u.name || u.email,
          roles, portals: Array.isArray(u.portals) ? u.portals : null, footballOpsPermissions: u.footballOpsPermissions || null,
        };
        const sees = inPortals(request, buildAuthValue({ currentUser: null, userRoles: roles, userPortalsRaw: viewer.portals, footballOpsStored: null }).userPortals);
        return { key: `football_ops:${u.id}`, viewer, note: sees ? null : "Can't see this tour (not in their portals)" };
      }),
    },
  ];

  const selected = groups.flatMap((g) => g.viewers).find((v) => v.key === selectedKey);

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-[14px] border border-[#E5E7EB] p-5">
        <h3 className="text-[14.5px] font-bold text-[#111827]">Previews</h3>
        <p className="text-[12.5px] text-[#6B7280] mt-1 max-w-[640px]">
          See this tour exactly as someone else sees it in their portal, with their permissions. Previews are read-only, so nothing you click changes the tour.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 1080:grid-cols-4 gap-4 mt-4">
          {groups.map((g) => (
            <div key={g.title}>
              <p className="text-[11px] font-bold text-[#9CA3AF] uppercase tracking-wider mb-2">{g.title}</p>
              {g.viewers.length === 0 ? (
                <p className="text-[12px] text-[#9CA3AF]">{g.empty}</p>
              ) : (
                <div className="space-y-1.5">
                  {g.viewers.map((v) => (
                    <button
                      key={v.key}
                      onClick={() => setSelectedKey(selectedKey === v.key ? null : v.key)}
                      className={`w-full text-left px-3 py-2 rounded-[9px] border transition ${
                        selectedKey === v.key ? 'bg-navy border-navy text-white' : 'bg-white border-[#E5E7EB] text-[#374151] hover:border-[#9CA3AF]'
                      }`}
                    >
                      <span className="block text-[12.5px] font-semibold truncate">{v.viewer.name || v.viewer.email}</span>
                      {v.note && (
                        <span className={`block text-[11px] ${selectedKey === v.key ? 'text-white/70' : 'text-[#D97706]'}`}>{v.note}</span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      {selected && <PortalPreviewFrame key={selected.key} viewer={selected.viewer} requestId={requestId} />}
    </div>
  );
}
